import { useEffect, useState } from "react";
import { Trash2, Plus, Loader2, KeyRound, UserPlus } from "lucide-react";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { bnNum } from "@/constants/site";

const inputCls = "w-full rounded-xl bg-[#2B0F15] border border-[#F2B134]/20 px-4 py-2.5 text-sm text-[#FDF6EA] placeholder:text-[#D1BBA7]/50 outline-none focus:border-[#F2B134]/70 transition-colors";

export function MembersTab() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", role_label: "Member", phone: "", account_role: "member", password: "" });
  const [busy, setBusy] = useState(false);
  const [pwEdit, setPwEdit] = useState({});
  const [requests, setRequests] = useState([]);
  const [pwReq, setPwReq] = useState({});

  const load = () => API.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
  const loadReq = () => API.get("/admin/signup-requests").then(({ data }) => setRequests(data)).catch(() => {});
  useEffect(() => { load(); loadReq(); }, []);

  const approve = async (r) => {
    try {
      await API.post(`/admin/signup-requests/${r.id}/approve`, { password: pwReq[r.id] || "", account_role: "member", role_label: "Member" });
      toast.success(`${r.name} অনুমোদিত — এখন লগইন করতে পারবেন`);
      setPwReq({ ...pwReq, [r.id]: "" });
      loadReq();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const reject = async (r) => {
    try {
      await API.delete(`/admin/signup-requests/${r.id}`);
      toast.success("অনুরোধ বাদ দেওয়া হয়েছে");
      loadReq();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/admin/members", { ...form, password: form.password });
      toast.success(`${form.name} যোগ হয়েছেন — এখন লগইন করতে পারবেন`);
      setForm({ name: "", role_label: "Member", phone: "", account_role: "member", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (m, payload, msg) => {
    try {
      await API.patch(`/admin/members/${m.id}`, payload);
      toast.success(msg || "সম্পাদিত হয়েছে");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (m) => {
    try {
      await API.delete(`/admin/members/${m.id}`);
      toast.success(`${m.name} বাদ দেওয়া হয়েছে`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="members-admin-tab">
      {requests.length > 0 && (
        <div className="rounded-3xl border-2 border-[#F2B134]/40 bg-[#1E0A0E] p-6 spotlight" data-testid="signup-requests-panel">
          <p className="font-bengali font-bold text-lg flex items-center gap-2">
            <UserPlus size={18} className="text-[#F2B134]" /> নতুন সদস্যের অনুরোধ ({bnNum(requests.length)})
          </p>
          <div className="mt-4 space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="rounded-2xl border border-[#F2B134]/20 bg-[#2B0F15] p-4" data-testid={`signup-request-${r.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-bengali font-bold">{r.name}</p>
                    <p className="text-xs text-[#D1BBA7]/80 tabular-nums">+91 {r.phone}{r.email ? ` · ${r.email}` : ""}</p>
                    {r.reason && <p className="font-bengali text-sm text-[#D1BBA7]/80 mt-1">{r.reason}</p>}
                  </div>
                  <input
                    type="password"
                    minLength={6}
                    placeholder="পাসওয়ার্ড সেট করুন"
                    value={pwReq[r.id] || ""}
                    onChange={(e) => setPwReq({ ...pwReq, [r.id]: e.target.value })}
                    data-testid={`signup-password-input-${r.id}`}
                    className={`${inputCls} w-44`}
                  />
                  <button
                    onClick={() => approve(r)}
                    disabled={!pwReq[r.id] || pwReq[r.id].length < 6}
                    data-testid={`signup-approve-btn-${r.id}`}
                    className="btn-gold rounded-full px-5 py-2.5 text-xs font-bold disabled:opacity-40"
                  >
                    অনুমোদন করুন
                  </button>
                  <button
                    onClick={() => reject(r)}
                    data-testid={`signup-reject-btn-${r.id}`}
                    className="h-10 w-10 rounded-xl border border-[#D7261E]/40 grid place-items-center text-[#FFB4AE] hover:bg-[#D7261E]/20"
                    title="বাদ দিন"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <form onSubmit={add} className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6" data-testid="member-add-form">
        <p className="font-bengali font-bold flex items-center gap-2"><Plus size={16} className="text-[#F2B134]" /> নতুন সদস্য যোগ করুন (লগইন অ্যাক্সেস সহ)</p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input required placeholder="নাম" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-add-name-input" className={inputCls} />
          <input placeholder="পদবি (যেমন Joint Secretary)" value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} data-testid="member-add-role-input" className={inputCls} />
          <input placeholder="ফোন (লগইন আইডি)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="member-add-phone-input" className={inputCls} />
          <input required type="password" minLength={6} placeholder="পাসওয়ার্ড" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-add-password-input" className={inputCls} />
          <select value={form.account_role} onChange={(e) => setForm({ ...form, account_role: e.target.value })} data-testid="member-add-role-select" className={inputCls}>
            <option value="member" className="bg-[#1E0A0E]">সদস্য (পোস্ট করতে পারবেন)</option>
            <option value="admin" className="bg-[#1E0A0E]">অ্যাডমিন (সব নিয়ন্ত্রণ)</option>
          </select>
        </div>
        <button type="submit" disabled={busy} data-testid="member-add-submit-btn" className="btn-gold mt-5 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50">
          {busy ? "যোগ হচ্ছে..." : "যোগ করুন"}
        </button>
      </form>

      <div className="space-y-3" data-testid="members-admin-list">
        {members.map((m) => (
          <div key={m.id} className="rounded-2xl border border-[#F2B134]/15 bg-[#1E0A0E] p-4" data-testid={`member-row-${m.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <p className="font-bengali font-bold">{m.name} {m.account_role === "admin" && <span className="text-[10px] font-bold text-[#F2B134] border border-[#F2B134]/40 rounded-full px-2 py-0.5 ml-1">অ্যাডমিন</span>}</p>
                <p className="text-xs text-[#D1BBA7]/70">{m.role_label}{m.phone ? ` · +91 ${m.phone}` : " · লগইন নেই"}</p>
              </div>
              <select
                value={m.account_role || "none"}
                onChange={(e) => patch(m, { account_role: e.target.value === "none" ? "" : e.target.value }, "ভূমিকা বদলানো হয়েছে")}
                data-testid={`member-role-select-${m.id}`}
                className={`${inputCls} w-auto`}
              >
                <option value="admin" className="bg-[#1E0A0E]">অ্যাডমিন</option>
                <option value="member" className="bg-[#1E0A0E]">সদস্য</option>
                <option value="none" className="bg-[#1E0A0E]">অ্যাক্সেস নেই</option>
              </select>
              <div className="flex items-center gap-1">
                <input
                  type="password"
                  placeholder="নতুন পাসওয়ার্ড"
                  value={pwEdit[m.id] || ""}
                  onChange={(e) => setPwEdit({ ...pwEdit, [m.id]: e.target.value })}
                  data-testid={`member-password-input-${m.id}`}
                  className={`${inputCls} w-36`}
                />
                <button
                  onClick={() => patch(m, { password: pwEdit[m.id] }, "পাসওয়ার্ড রিসেট হয়েছে")}
                  disabled={!pwEdit[m.id] || pwEdit[m.id].length < 6}
                  data-testid={`member-reset-password-btn-${m.id}`}
                  className="h-10 w-10 rounded-xl border border-[#F2B134]/30 grid place-items-center text-[#F2B134] disabled:opacity-40"
                  title="পাসওয়ার্ড রিসেট"
                >
                  <KeyRound size={14} />
                </button>
              </div>
              <button onClick={() => del(m)} data-testid={`member-delete-btn-${m.id}`} className="h-10 w-10 rounded-xl border border-[#D7261E]/40 grid place-items-center text-[#FFB4AE] hover:bg-[#D7261E]/20">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SponsorsTab() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({ name: "", tier: "well_wisher", website: "", message: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [donation, setDonation] = useState({ upi_id: "", note: "" });
  const [savingDonation, setSavingDonation] = useState(false);

  const load = () => API.get("/admin/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
  useEffect(() => {
    load();
    API.get("/settings/donation").then(({ data }) => setDonation(data)).catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let logo_url = "";
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const { data } = await API.post("/upload", fd);
        logo_url = data.url;
      }
      await API.post("/admin/sponsors", { ...form, logo_url });
      toast.success("স্পনসর যোগ হয়েছে");
      setForm({ name: "", tier: "well_wisher", website: "", message: "" });
      setLogoFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveDonation = async (e) => {
    e.preventDefault();
    setSavingDonation(true);
    try {
      await API.put("/admin/settings/donation", donation);
      toast.success("দানের সেটিংস সংরক্ষিত — ডিজিটাল অঞ্জলি এখন ওয়েবসাইটে দেখা যাবে");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingDonation(false);
    }
  };

  const del = async (s) => {
    try {
      await API.delete(`/admin/sponsors/${s.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="sponsors-admin-tab">
      <form onSubmit={add} className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6" data-testid="sponsor-add-form">
        <p className="font-bengali font-bold">নতুন স্পনসর</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <input required placeholder="স্পনসরের নাম" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-add-name-input" className={inputCls} />
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="sponsor-add-tier-select" className={inputCls}>
            <option value="title" className="bg-[#1E0A0E]">টাইটেল স্পনসর</option>
            <option value="patron" className="bg-[#1E0A0E]">প্যাট্রন</option>
            <option value="well_wisher" className="bg-[#1E0A0E]">শুভানুধ্যায়ী</option>
          </select>
          <input placeholder="ওয়েবসাইট (ঐচ্ছিক)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="sponsor-add-website-input" className={inputCls} />
          <input placeholder="শুভেচ্ছাবার্তা (ঐচ্ছিক)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sponsor-add-message-input" className={`${inputCls} font-bengali`} />
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-[#F2B134]/80 font-semibold">লোগো (ঐচ্ছিক)</label>
            <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="sponsor-add-logo-input" className="mt-2 w-full text-sm text-[#D1BBA7] file:mr-4 file:rounded-full file:border-0 file:bg-[#F2B134] file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#2A0A0E] file:cursor-pointer" />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="sponsor-add-submit-btn" className="btn-gold mt-5 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : "যোগ করুন"}
        </button>
      </form>

      <form onSubmit={saveDonation} className="rounded-3xl border border-[#F2B134]/20 bg-[#1E0A0E] p-6" data-testid="donation-settings-form">
        <p className="font-bengali font-bold">দানের সেটিংস — ডিজিটাল অঞ্জলি (UPI)</p>
        <p className="text-xs text-[#D1BBA7]/70 mt-1 font-bengali">ইউপিআই আইডি যোগ করলে ওয়েবসাইটে অনুদান সেকশনে QR কোড দেখা যাবে।</p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <input placeholder="ইউপিআই আইডি (যেমন 9051933331@ybl)" value={donation.upi_id} onChange={(e) => setDonation({ ...donation, upi_id: e.target.value })} data-testid="donation-settings-upi-input" className={`${inputCls} font-mono`} />
          <input placeholder="নোট (ঐচ্ছিক)" value={donation.note} onChange={(e) => setDonation({ ...donation, note: e.target.value })} data-testid="donation-settings-note-input" className={`${inputCls} font-bengali`} />
        </div>
        <button type="submit" disabled={savingDonation} data-testid="donation-settings-save-btn" className="btn-gold mt-5 rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-50">
          {savingDonation ? "সংরক্ষণ হচ্ছে..." : "সংরক্ষণ করুন"}
        </button>
      </form>

      <div className="space-y-3" data-testid="sponsors-admin-list">
        {sponsors.map((s) => (
          <div key={s.id} className="flex items-center gap-4 rounded-2xl border border-[#F2B134]/15 bg-[#1E0A0E] p-4" data-testid={`sponsor-row-${s.id}`}>
            {s.logo_url ? (
              <img src={s.logo_url} alt={s.name} className="h-10 w-16 object-contain rounded" />
            ) : (
              <span className="h-10 w-10 rounded-full grid place-items-center font-bold text-[#2A0A0E] bg-[linear-gradient(135deg,#FFE9A8,#F2B134_55%,#D99A1F)] shrink-0">
                {s.name.slice(0, 1)}
              </span>
            )}
            <div className="flex-1">
              <p className="font-bengali font-bold">{s.name}</p>
              <p className="text-xs text-[#D1BBA7]/70">{s.tier}{s.message ? ` · ${s.message}` : ""}</p>
            </div>
            <button onClick={() => del(s)} data-testid={`sponsor-delete-btn-${s.id}`} className="h-9 w-9 rounded-xl border border-[#D7261E]/40 grid place-items-center text-[#FFB4AE] hover:bg-[#D7261E]/20">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InboxTab() {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    API.get("/admin/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-3xl space-y-3" data-testid="inbox-tab">
      {messages.length === 0 && <p className="font-bengali text-[#D1BBA7]/70">ইনবক্স খালি — ওয়েবসাইটের যোগাযোগ ফর্ম থেকে বার্তা এলে এখানে দেখা যাবে।</p>}
      {messages.map((m) => (
        <div key={m.id} className="rounded-2xl border border-[#F2B134]/15 bg-[#1E0A0E] p-5" data-testid={`inbox-message-${m.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-bengali font-bold">{m.name} {m.phone && <span className="text-xs text-[#D1BBA7]/70 tabular-nums font-normal">· +91 {m.phone}</span>}</p>
            <p className="text-xs text-[#D1BBA7]/60 tabular-nums">{bnNum((m.created_at || "").slice(0, 10))}</p>
          </div>
          <p className="font-bengali text-sm text-[#D1BBA7] mt-2 leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

export function ActivityTab() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    API.get("/admin/activity", { params: { limit: 80 } }).then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-3xl space-y-2" data-testid="activity-tab">
      {items.length === 0 && <p className="font-bengali text-[#D1BBA7]/70">এখনও কোনও কার্যক্রম নেই।</p>}
      {items.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-4 rounded-xl border border-[#F2B134]/12 bg-[#1E0A0E] px-4 py-3" data-testid={`activity-row-${a.id}`}>
          <p className="text-sm"><span className="font-bold text-[#F2B134]">{a.actor_name}</span> <span className="font-bengali text-[#D1BBA7]">{a.detail || a.action}</span></p>
          <p className="text-xs text-[#D1BBA7]/50 tabular-nums shrink-0">{bnNum((a.created_at || "").slice(0, 10))}</p>
        </div>
      ))}
    </div>
  );
}
